import Decimal from "decimal.js";
import { BUYDOWN_REDUCTIONS, STEP_PAYMENT } from "../constants/buydown";

// Standard monthly payment (amortization). ir = monthly rate (decimal),
// n = number of months, pv = present value (positive loan amount).
function pmt(ir, n, pv) {
  if (ir.eq(0)) return pv.div(n);
  const factor = ir.plus(1).pow(n);
  return pv.mul(ir).mul(factor).div(factor.minus(1));
}

export function is_buydown(loan) {
  return !!(loan && loan.buydown_type && BUYDOWN_REDUCTIONS[loan.buydown_type]);
}

// Rate reductions (in %) per stepped year, e.g. 3-2-1 -> [3, 2, 1].
export function buydown_reductions(loan) {
  return BUYDOWN_REDUCTIONS[loan.buydown_type] || [];
}

// Number of stepped (bought-down) years.
export function buydown_year_count(loan) {
  return buydown_reductions(loan).length;
}

// Months per step (manual, default 12).
export function buydown_step_months(loan) {
  const m = parseInt(loan.buydown_step_months, 10);
  return isNaN(m) || m <= 0 ? 12 : m;
}

// Total months the buydown runs (e.g. 3-2-1 at 12/step = 36).
export function buydown_total_months(loan) {
  return buydown_year_count(loan) * buydown_step_months(loan);
}

// The first year the borrower pays the full note rate (e.g. 3-2-1 -> Year 4).
export function buydown_full_rate_year(loan) {
  return buydown_year_count(loan) + 1;
}

// Interest rate the borrower pays in a given 1-based year.
export function buydown_year_rate(loan, year) {
  const reductions = buydown_reductions(loan);
  const note = new Decimal(loan.rate || "0");
  const reduction = year <= reductions.length ? reductions[year - 1] : 0;
  return note.sub(reduction);
}

// Monthly P&I for a given year — full amortization at that year's rate on the
// original loan amount over the full term.
export function buydown_year_payment(loan, year, loanAmount, termMonths) {
  const monthlyRate = buydown_year_rate(loan, year).div(1200);
  return pmt(monthlyRate, new Decimal(termMonths), new Decimal(loanAmount || "0"));
}

// Product line shown on the LE (auto-generated).
//  Step Payment -> "Year N Step Payment, Fixed Rate"  (N = number of stepped years:
//                  3-2-1 -> 3, 2-1/1-1 -> 2, 1-0 -> 1)
//  Step Rate    -> "0/1 Step Rate" for multi-year buydowns; a single stepped year
//                  (1-0) discloses as "1/0 Step Rate".
export function buydown_product_line(loan) {
  if (!is_buydown(loan)) return "";
  if (loan.buydown_disclose_product === STEP_PAYMENT) {
    return `Year ${buydown_year_count(loan)} Step Payment, Fixed Rate`;
  }
  return buydown_year_count(loan) === 1 ? "1/0 Step Rate" : "0/1 Step Rate";
}

// Projected-payment columns: one per stepped year (Year 1..N) then the full-rate
// column (Years N+1 - 30). Each carries its month range for the Payment Calculation row.
export function buydown_payment_columns(loan) {
  const n = buydown_year_count(loan);
  const step = buydown_step_months(loan);
  const cols = [];
  for (let y = 1; y <= n; y++) {
    cols.push({
      label: `Year ${y}`,
      year: y,
      monthStart: (y - 1) * step + 1,
      monthEnd: y * step
    });
  }
  cols.push({
    label: `Years ${n + 1} - 30`,
    year: n + 1,
    isFull: true,
    monthStart: n * step + 1,
    monthEnd: 360
  });
  return cols;
}

// Month (and payment number) at which the first rate/payment change takes effect
// (step_months + 1, e.g. 13).
export function buydown_first_change_month(loan) {
  return buydown_step_months(loan) + 1;
}

// Payment number at which the full note-rate payment begins (N * step + 1, e.g. 37).
export function buydown_full_payment_number(loan) {
  return buydown_year_count(loan) * buydown_step_months(loan) + 1;
}

// Payment numbers at which the P&I steps up, e.g. 3-2-1 at 12/step -> [13, 25, 37].
export function buydown_change_payments(loan) {
  const n = buydown_year_count(loan);
  const step = buydown_step_months(loan);
  const out = [];
  for (let k = 1; k <= n; k++) out.push(k * step + 1);
  return out;
}

// Per-year rate increases (in %). e.g. 3-2-1 -> [1, 1, 1]; 1-1 -> [0, 1]; 1-0 -> [1].
export function buydown_rate_steps(loan) {
  const seq = [...buydown_reductions(loan), 0];
  const steps = [];
  for (let i = 1; i < seq.length; i++) steps.push(seq[i - 1] - seq[i]);
  return steps;
}

// AIR "Limits on Interest Rate Changes" — first and subsequent change caps (in %).
export function buydown_first_rate_step(loan) {
  const s = buydown_rate_steps(loan);
  return s.length ? s[0] : 1;
}

export function buydown_subsequent_rate_step(loan) {
  const s = buydown_rate_steps(loan);
  if (s.length <= 1) return s.length ? s[0] : 1;
  return Math.max(...s.slice(1));
}
